import { ChangeDetectionStrategy, Component, computed, input, linkedSignal } from "@angular/core";
import { S25IngestionFieldRulesComponent } from "../../s25-rule-tree/s25.ingestion.field.rules.component";
import { S25ToggleButtonModule } from "../../s25-toggle-button/s25.toggle.button.module";
import { Workflow } from "../service/Workflow";
import { INGESTION_POINT_TYPE_ID } from "../../data-ingestion/data.ingestion.const";
import { AriaLabel } from "../../../util/aria/aria.label";

function newActionRuleKey(): number {
    return -Math.floor(1_000_000_000 + Math.random() * 1_000_000_000);
}

@Component({
    standalone: true,
    selector: "s25-ng-workflow-set-security-group-action",
    template: `
        <p class="help">
            @if (showRules()) {
                If the form has a Security Group answer, that group is used. Otherwise the first matching rule below
                supplies the group. The form submitter is moved into that one security group.
            } @else {
                The form's Security Group answer is used. The form submitter is moved into that one security group.
            }
        </p>
        @if (isNewAction()) {
            <div class="use-rules">
                <span class="use-rules-label" [id]="useRulesLabelId">
                    Use rules when the form has no Security Group answer
                </span>
                <s25-toggle-button
                    [value]="useRules()"
                    (valueChange)="onUseRules($event)"
                    [toggleLabel]="useRulesToggleLabel"
                />
            </div>
        }
        @if (showRules()) {
            <s25-ng-ingestion-field-rules
                [category]="'workflowActionInput'"
                [subCategory]="subCategory()"
                [subCategoryPrefix]="subCategoryPrefix()"
                [formId]="formId()"
                [searchCriteriaType]="'securityGroups'"
                [picker]="'dropdown'"
            />
        }
    `,
    styles: `
        .help {
            margin: 0 0 16px;
            color: var(--color--text-muted);
            font-size: 13px;
        }

        .use-rules {
            display: flex;
            align-items: center;
            gap: 12px;
            margin: 0 0 16px;
        }

        .use-rules-label {
            color: var(--color--text);
        }
    `,
    imports: [S25IngestionFieldRulesComponent, S25ToggleButtonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetSecurityGroupActionComponent {
    private static nextLabelId = 0;

    readonly action = input.required<Workflow.WorkflowAction>();
    readonly useRulesLabelId = `security-group-use-rules-${++SetSecurityGroupActionComponent.nextLabelId}`;
    readonly useRulesToggleLabel: AriaLabel = { by: this.useRulesLabelId };

    /** New actions have no saved id, so rules need a client key. Seed from one already chosen. */
    readonly useRules = linkedSignal(() => {
        const action = this.action();
        return action.workflowActionId == null && action.ruleKey != null;
    });

    readonly isNewAction = computed(() => this.action().workflowActionId == null);

    readonly showRules = computed(() => !this.isNewAction() || this.useRules());

    readonly formId = computed(() => {
        const action = this.action();
        if (Number(action.actionItemTypeId) !== INGESTION_POINT_TYPE_ID) return undefined;
        return action.actionItemId;
    });

    readonly actionKey = computed(() => {
        const action = this.action();
        return action.workflowActionId ?? action.ruleKey;
    });

    readonly subCategory = computed(() => {
        const actionKey = this.actionKey();
        const formId = this.formId();
        if (actionKey == null || formId == null) return undefined;
        return `${actionKey}&${INGESTION_POINT_TYPE_ID}&${formId}`;
    });

    readonly subCategoryPrefix = computed(() => {
        const actionKey = this.actionKey();
        if (actionKey == null) return undefined;
        return `${actionKey}&`;
    });

    onUseRules(enabled: boolean | undefined): void {
        const useRules = enabled === true;
        this.useRules.set(useRules);
        const action = this.action();
        if (action.workflowActionId != null) return;
        action.ruleKey = useRules ? (action.ruleKey ?? newActionRuleKey()) : undefined;
    }
}
